/**
 * GiftModal — Sprint 3 §3c
 * Turns a spot (or micro-itinerary) into a self-contained gift link and offers
 * to share it (Web Share / copy) plus a scannable QR. Fully offline for the
 * link itself; the QR image uses the same external renderer as ShareModal.
 *
 * If the payload is too big to travel inline it is CAPPED (we ask to trim) —
 * no server short-code (confirmed offline-only).
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { encodeGift } from '../utils/giftManager';
import theme from '../styles/theme';

export default function GiftModal({ gift, onClose }) {
  const { t } = useTranslation('common');
  const [copied, setCopied] = useState(false);

  const result = useMemo(() => encodeGift(gift), [gift]);
  const title = gift?.data?.name || gift?.data?.title || t('gift.aPlace');
  const qrUrl = result.ok
    ? `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(result.url)}`
    : '';

  const share = async () => {
    if (!result.ok) return;
    try {
      if (navigator.share) {
        await navigator.share({ title: t('gift.shareTitle', { name: title }), text: t('gift.shareText'), url: result.url });
      } else {
        copy();
      }
    } catch { /* user cancelled — fine */ }
  };

  const copy = async () => {
    if (!result.ok) return;
    try {
      await navigator.clipboard.writeText(result.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* clipboard blocked */ }
  };

  return (
    <div style={S.backdrop} onClick={e => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div style={S.sheet}>
        <div style={S.handle} />
        <div style={S.eyebrow}>🎁 {t('gift.title')}</div>
        <h2 style={S.name}>{title}</h2>

        {!result.ok ? (
          <div style={S.capNotice}>
            {result.reason === 'too_big' ? t('gift.tooBig') : t('gift.empty')}
          </div>
        ) : (
          <>
            <p style={S.desc}>{t('gift.description')}</p>
            {qrUrl && (
              <div style={S.qrWrap}>
                <img src={qrUrl} alt={t('gift.qrAlt')} loading="lazy" style={S.qr} />
              </div>
            )}
            <button type="button" style={S.shareBtn} onClick={share}>
              {t('gift.share')}
            </button>
            <button type="button" style={S.copyBtn} onClick={copy}>
              {copied ? t('gift.copied') : t('gift.copy')}
            </button>
          </>
        )}

        <button type="button" style={S.close} onClick={onClose}>{t('gift.close')}</button>
      </div>
    </div>
  );
}

const S = {
  backdrop: { position: 'fixed', inset: 0, zIndex: 320, background: 'rgba(26,51,64,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' },
  sheet: { background: theme.cream, width: '100%', maxWidth: 480, borderRadius: '20px 20px 0 0', padding: '12px 20px 28px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 -8px 32px rgba(26,51,64,0.2)', textAlign: 'center' },
  handle: { width: 40, height: 4, borderRadius: 2, background: theme.sand, margin: '0 auto 14px' },
  eyebrow: { fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: theme.terracottaDark },
  name: { fontSize: 20, fontWeight: 800, color: theme.brownDark, margin: '6px 0 4px' },
  desc: { fontSize: 13, color: theme.brown, fontStyle: 'italic', margin: '0 0 16px' },
  capNotice: { fontSize: 14, color: theme.brownDark, background: '#fff8f0', border: `1.5px solid ${theme.terracotta}`, borderRadius: 12, padding: '14px', margin: '8px 0 16px', lineHeight: 1.5 },
  qrWrap: { display: 'flex', justifyContent: 'center', marginBottom: 16 },
  qr: { width: 200, height: 200, borderRadius: 12, border: `1px solid ${theme.bluePale}`, background: '#fff', padding: 8 },
  shareBtn: { width: '100%', padding: '14px', borderRadius: 12, border: 'none', background: theme.terracotta, color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer', marginBottom: 10 },
  copyBtn: { width: '100%', padding: '13px', borderRadius: 12, border: `1.5px solid ${theme.blue}`, background: 'transparent', color: theme.blueDeep, fontSize: 14, fontWeight: 600, cursor: 'pointer', marginBottom: 10 },
  close: { width: '100%', padding: '12px', borderRadius: 12, border: 'none', background: 'transparent', color: theme.brown, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
};
